import React, { useEffect, useState } from 'react';
import { Button, Input, InputPassword, Modal, SparkModalPortal, sparkToast } from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import RecommendedProviderRow from '@/features/settings/components/RecommendedProviderRow.jsx';
import '../settings.css';

const FIELD_DEFS = [
    {
        name: 'aws_access_key_id',
        label: 'AWS Access Key ID',
        required: true,
        secret: false,
        placeholder: 'AKIA...',
    },
    {
        name: 'aws_secret_access_key',
        label: 'AWS Secret Access Key',
        required: true,
        secret: true,
        placeholder: 'Enter your AWS secret access key',
    },
    {
        name: 'aws_region',
        label: 'AWS Region',
        required: true,
        secret: false,
        placeholder: 'us-east-1',
    },
    {
        name: 'aws_s3_bucket',
        label: 'AWS S3 Bucket',
        required: true,
        secret: false,
        placeholder: 'my-models-bucket',
    },
    {
        name: 'aws_role_arn',
        label: 'AWS Role ARN',
        required: true,
        secret: false,
        placeholder: 'arn:aws:iam::123456789012:role/...',
    },
];

export const BEDROCK_REQUIRED_FIELDS = FIELD_DEFS.filter((f) => f.required).map(
    (f) => f.name
);

const BedrockCredentialsForm = ({
    credentials,
    onCredentialsUpdate,
    onCredentialsClear,
    recommendedProvider,
    onRecommendedProviderUpdate,
}) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const [values, setValues] = useState(credentials || {});
    const [isSaving, setIsSaving] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [confirmingClear, setConfirmingClear] = useState(false);

    useEffect(() => {
        setValues(credentials || {});
    }, [credentials]);

    const isDirty = FIELD_DEFS.some(
        (f) => (values[f.name] || '') !== (credentials?.[f.name] || '')
    );

    const hasSavedCredentials = FIELD_DEFS.some((f) => Boolean(credentials?.[f.name]));

    const handleChange = (name, value) => {
        setValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        const trimmedValues = FIELD_DEFS.reduce((acc, f) => {
            acc[f.name] = (values[f.name] || '').trim();
            return acc;
        }, {});

        const missingFields = FIELD_DEFS.filter(
            (f) => f.required && !trimmedValues[f.name]
        );
        if (missingFields.length > 0) {
            sparkToast.error(
                'Missing required fields',
                { content: missingFields.map((f) => f.label).join(', ') }
            );
            return;
        }

        setIsSaving(true);
        onCredentialsUpdate(trimmedValues, 'saved');
        setTimeout(() => setIsSaving(false), 500);
    };

    const handleClear = () => {
        setConfirmingClear(false);
        setIsClearing(true);
        onCredentialsClear();
        setTimeout(() => setIsClearing(false), 500);
    };

    return (
        <div className="settings-providers__panel">
            <div className="settings-subheading-group">
                <h3 className="settings-subheading fs-unmask">Bedrock Credentials</h3>
                <p className="settings-text settings-providers__description fs-unmask">
                    Configure AWS credentials so SpecificAI can call Bedrock-hosted
                    models (Anthropic, DeepSeek, etc.). Need help? See the
                    {' '}
                    <a
                        className="settings-link"
                        href="https://docs.aws.amazon.com/bedrock/latest/userguide/setting-up.html"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Bedrock setup guide
                    </a>
                    .
                </p>
            </div>

            <div className="settings-form">
                {FIELD_DEFS.map((field) => (
                    field.secret ? (
                        <InputPassword
                            key={field.name}
                            size="large"
                            label={field.label}
                            showAsterisk={field.required}
                            showPrefixIcon
                            value={values[field.name] || ''}
                            onChange={(e) => handleChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                            disabled={!isAdmin}
                            className="settings-secret__input"
                        />
                    ) : (
                        <Input
                            key={field.name}
                            size="large"
                            label={field.label}
                            showAsterisk={field.required}
                            value={values[field.name] || ''}
                            onChange={(e) => handleChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                            disabled={!isAdmin}
                            className="settings-secret__input"
                        />
                    )
                ))}
            </div>

            <div className="settings-form__footer settings-form__footer--gap">
                <Button
                    size="tiny"
                    variant="secondary"
                    danger
                    disabled={!isAdmin || !hasSavedCredentials || isClearing}
                    disableWhenReadOnly
                    onClick={() => setConfirmingClear(true)}
                >
                    Clear
                </Button>
                <Button
                    size="tiny"
                    variant="primary"
                    disabled={!isAdmin || !isDirty || isSaving}
                    disableWhenReadOnly
                    onClick={handleSave}
                >
                    Save Changes
                </Button>
            </div>

            {confirmingClear ? (
                <SparkModalPortal
                    open
                    onClose={() => setConfirmingClear(false)}
                    size="text"
                >
                    <Modal
                        title="Clear Bedrock credentials?"
                        subtitle="This removes all saved AWS Bedrock credentials from your organization."
                        showScrollBar={false}
                        fixedHeight={false}
                        showOverlay={false}
                        danger
                        primaryLabel="Clear"
                        tertiaryLabel="Cancel"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={handleClear}
                        onTertiaryClick={() => setConfirmingClear(false)}
                        onCloseClick={() => setConfirmingClear(false)}
                    />
                </SparkModalPortal>
            ) : null}

            <RecommendedProviderRow
                provider="bedrock"
                recommendedProvider={recommendedProvider}
                onRecommendedProviderUpdate={onRecommendedProviderUpdate}
            />
        </div>
    );
};

export default BedrockCredentialsForm;
